// components/ProteinStructureViewer.tsx
import React, { useEffect, useRef, useState, useCallback } from 'react';

// Type declaration for 3Dmol
declare global {
  interface Window {
    $3Dmol: any;
  }
}

interface ProteinStructureViewerProps {
  geneSymbol: string;
  proteinChange?: string | null;
  uniprotId?: string | null;
  height?: number;
}

// Gene to UniProt ID mapping for common genes
const GENE_TO_UNIPROT: Record<string, string> = {
  // Tumor Suppressors
  TP53: 'P04637',
  BRCA1: 'P38398',
  BRCA2: 'P51587',
  PTEN: 'P60484',
  RB1: 'P06400',
  APC: 'P25054',
  NF1: 'P21359',
  NF2: 'P35240',
  
  // Cardiomyopathy Genes
  MYH7: 'P12883',
  MYBPC3: 'Q14896',
  TNNT2: 'P45379',
  TNNI3: 'P19429',
  TPM1: 'P09493',
  ACTC1: 'P68032',
  MYL2: 'P10916',
  MYL3: 'P08590',
  LMNA: 'P02545',
  TTN: 'Q8WZ42',
  DES: 'P17661',
  FLNC: 'Q14315',
  BAG3: 'O95817',
  RBM20: 'Q5T481',
  PLN: 'P26678',
  
  // Arrhythmia Genes
  PKP2: 'Q99959',
  DSP: 'P15924',
  DSC2: 'Q02487',
  DSG2: 'Q14126',
  JUP: 'P14923',
  TMEM43: 'Q9BTV4',
  SCN5A: 'Q14524',
  KCNH2: 'Q12809',
  KCNQ1: 'P51787',
  KCNE1: 'P15382',
  KCNE2: 'Q9Y6J6',
  RYR2: 'Q92736',
  CASQ2: 'O14958',
  CALM1: 'P0DP23',
  CACNA1C: 'Q13936',
  
  // Other Common Genes
  CFTR: 'P13569',
  DMD: 'P11532',
  FBN1: 'P35555',
  LDLR: 'P01130',
  BRAF: 'P15056',
  EGFR: 'P00533',
  KRAS: 'P01116',
  
  // Neurological
  MECP2: 'P51608',
  SCN1A: 'P35498',
  SCN2A: 'Q99250',
  APP: 'P05067',
};

interface StructureInfo {
  uniprotId: string;
  variantPosition?: number;
  refAA?: string;
  altAA?: string;
  structureSource?: string;
}

type ViewStyle = 'cartoon' | 'stick' | 'sphere' | 'surface';

const ProteinStructureViewer: React.FC<ProteinStructureViewerProps> = ({
  geneSymbol,
  proteinChange,
  uniprotId: providedUniprotId,
  height = 400,
}) => {
  const viewerRef = useRef<HTMLDivElement>(null);
  const viewerInstance = useRef<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [structureInfo, setStructureInfo] = useState<StructureInfo | null>(null);
  const [is3DmolLoaded, setIs3DmolLoaded] = useState(false);
  const [currentStyle, setCurrentStyle] = useState<ViewStyle>('cartoon');
  const [isSpinning, setIsSpinning] = useState(false);
  const [showSurface, setShowSurface] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState<string>('Initializing...');

  // Load 3Dmol.js dynamically
  useEffect(() => {
    const load3Dmol = async () => {
      if (window.$3Dmol) {
        setIs3DmolLoaded(true);
        return;
      }

      try {
        // Dynamic import for Vite compatibility
        const module = await import('3dmol');
        window.$3Dmol = module.default || module;
        setIs3DmolLoaded(true);
        console.log('✅ 3Dmol loaded via dynamic import');
      } catch (err) {
        console.warn('Dynamic import failed, trying CDN fallback:', err);
        // Fallback: Load via script tag
        const script = document.createElement('script');
        script.src = 'https://cdnjs.cloudflare.com/ajax/libs/3Dmol/2.1.0/3Dmol-min.js';
        script.async = true;
        script.onload = () => {
          console.log('✅ 3Dmol loaded via CDN');
          setIs3DmolLoaded(true);
        };
        script.onerror = () => {
          setError('Failed to load 3D visualization library');
          setLoading(false);
        };
        document.head.appendChild(script);
      }
    };

    load3Dmol();
  }, []);

  // Parse protein change (e.g., "R403Q" -> { position: 403, ref: "R", alt: "Q" })
  const parseProteinChange = useCallback((change: string | null | undefined) => {
    if (!change) return { position: undefined, refAA: undefined, altAA: undefined };
    
    // Handle formats: R403Q, Arg403Gln, p.R403Q, p.Arg403Gln
    const cleanChange = change.replace(/^p\./, '');
    
    // Single letter format: R403Q
    const singleMatch = cleanChange.match(/^([A-Z])(\d+)([A-Z*X])$/i);
    if (singleMatch) {
      return {
        position: parseInt(singleMatch[2]),
        refAA: singleMatch[1].toUpperCase(),
        altAA: singleMatch[3].toUpperCase(),
      };
    }
    
    // Three letter format: Arg403Gln
    const threeLetterMap: Record<string, string> = {
      Ala: 'A', Arg: 'R', Asn: 'N', Asp: 'D', Cys: 'C',
      Glu: 'E', Gln: 'Q', Gly: 'G', His: 'H', Ile: 'I',
      Leu: 'L', Lys: 'K', Met: 'M', Phe: 'F', Pro: 'P',
      Ser: 'S', Thr: 'T', Trp: 'W', Tyr: 'Y', Val: 'V',
      Ter: '*',
    };
    
    const threeMatch = cleanChange.match(/^([A-Z][a-z]{2})(\d+)([A-Z][a-z]{2}|Ter)$/);
    if (threeMatch) {
      return {
        position: parseInt(threeMatch[2]),
        refAA: threeLetterMap[threeMatch[1]] || threeMatch[1],
        altAA: threeLetterMap[threeMatch[3]] || threeMatch[3],
      };
    }
    
    return { position: undefined, refAA: undefined, altAA: undefined };
  }, []);

  // Get UniProt ID
  const getUniprotId = useCallback(async (gene: string): Promise<string | null> => {
    // First check our mapping
    const mapped = GENE_TO_UNIPROT[gene.toUpperCase()];
    if (mapped) {
      console.log(`📍 Found UniProt ID in mapping: ${gene} -> ${mapped}`);
      return mapped;
    }

    // If provided, use that
    if (providedUniprotId) {
      console.log(`📍 Using provided UniProt ID: ${providedUniprotId}`);
      return providedUniprotId;
    }

    // Try UniProt API
    try {
      setLoadingStatus('Looking up UniProt ID...');
      const response = await fetch(
        `https://rest.uniprot.org/uniprotkb/search?query=gene:${gene}+AND+organism_id:9606+AND+reviewed:true&format=json&size=1`
      );
      if (!response.ok) return null;
      const data = await response.json();
      const uniprotId = data.results?.[0]?.primaryAccession || null;
      console.log(`📍 Found UniProt ID via API: ${gene} -> ${uniprotId}`);
      return uniprotId;
    } catch (err) {
      console.warn('UniProt API lookup failed:', err);
      return null;
    }
  }, [providedUniprotId]);

  // Fetch PDB structure with multiple fallback sources
  const fetchStructure = useCallback(async (uniprotId: string): Promise<{ pdbData: string; source: string }> => {
    const sources = [
      {
        name: 'AlphaFold v4',
        url: `https://alphafold.ebi.ac.uk/files/AF-${uniprotId}-F1-model_v4.pdb`,
      },
      {
        name: 'AlphaFold v3',
        url: `https://alphafold.ebi.ac.uk/files/AF-${uniprotId}-F1-model_v3.pdb`,
      },
      {
        name: 'AlphaFold v2',
        url: `https://alphafold.ebi.ac.uk/files/AF-${uniprotId}-F1-model_v2.pdb`,
      },
      {
        name: 'AlphaFold API',
        url: `https://alphafold.ebi.ac.uk/api/prediction/${uniprotId}`,
        isApi: true,
      },
    ];

    for (const source of sources) {
      try {
        setLoadingStatus(`Fetching from ${source.name}...`);
        console.log(`🔄 Trying ${source.name}: ${source.url}`);

        if (source.isApi) {
          // Handle API response differently
          const response = await fetch(source.url);
          if (response.ok) {
            const data = await response.json();
            if (data && data[0]?.pdbUrl) {
              const pdbResponse = await fetch(data[0].pdbUrl);
              if (pdbResponse.ok) {
                const pdbData = await pdbResponse.text();
                console.log(`✅ Successfully fetched from ${source.name}`);
                return { pdbData, source: source.name };
              }
            }
          }
        } else {
          const response = await fetch(source.url);
          if (response.ok) {
            const pdbData = await response.text();
            // Verify it's actual PDB data
            if (pdbData.includes('ATOM') || pdbData.includes('HETATM')) {
              console.log(`✅ Successfully fetched from ${source.name}`);
              return { pdbData, source: source.name };
            }
          }
        }
      } catch (err) {
        console.warn(`❌ Failed to fetch from ${source.name}:`, err);
      }
    }

    throw new Error(`Structure not available for ${uniprotId}`);
  }, []);

  // Apply current style to viewer
  const applyStyle = useCallback((viewer: any, style: ViewStyle, variantPosition?: number) => {
    if (!viewer) return;

    viewer.setStyle({}, {}); // Clear existing styles

    const baseStyle: any = {};
    
    switch (style) {
      case 'cartoon':
        baseStyle.cartoon = {
          colorfunc: (atom: any) => {
            const bfactor = atom.b;
            if (bfactor > 90) return '#0053D6';
            if (bfactor > 70) return '#65CBF3';
            if (bfactor > 50) return '#FFDB13';
            return '#FF7D45';
          },
        };
        break;
      case 'stick':
        baseStyle.stick = { colorscheme: 'ssJmol' };
        break;
      case 'sphere':
        baseStyle.sphere = { colorscheme: 'Jmol', scale: 0.3 };
        break;
      case 'surface':
        baseStyle.cartoon = { color: 'spectrum' };
        break;
    }

    viewer.setStyle({}, baseStyle);

    // Highlight variant position
    if (variantPosition) {
      viewer.setStyle(
        { resi: variantPosition },
        {
          ...baseStyle,
          cartoon: { color: '#dc2626' },
          stick: { color: '#dc2626', radius: 0.2 },
        }
      );
      viewer.addStyle(
        { resi: variantPosition, atom: 'CA' },
        { sphere: { color: '#dc2626', radius: 1.0 } }
      );
    }

    if (showSurface) {
      viewer.addSurface(
        window.$3Dmol?.SurfaceType?.VDW || 1,
        { opacity: 0.7, color: 'white' },
        { hetflag: false }
      );
    }

    viewer.render();
  }, [showSurface]);

  // Initialize viewer
  useEffect(() => {
    if (!is3DmolLoaded || !viewerRef.current || !window.$3Dmol) return;

    let mounted = true;

    const initViewer = async () => {
      setLoading(true);
      setError(null);
      setLoadingStatus('Initializing viewer...');

      try {
        // Get UniProt ID
        const uniprotId = await getUniprotId(geneSymbol);
        if (!uniprotId) {
          throw new Error(`Could not find UniProt ID for ${geneSymbol}`);
        }

        // Parse variant position
        const { position, refAA, altAA } = parseProteinChange(proteinChange);
        
        // Fetch structure
        setLoadingStatus('Fetching protein structure...');
        const { pdbData, source } = await fetchStructure(uniprotId);

        if (!mounted) return;

        setStructureInfo({ 
          uniprotId, 
          variantPosition: position, 
          refAA, 
          altAA,
          structureSource: source 
        });

        // Clear previous viewer
        if (viewerInstance.current) {
          viewerInstance.current.clear();
          viewerInstance.current = null;
        }

        setLoadingStatus('Rendering 3D structure...');

        // Create viewer
        const viewer = window.$3Dmol.createViewer(viewerRef.current, {
          backgroundColor: '#f8fafc',
          antialias: true,
        });
        viewerInstance.current = viewer;

        // Add model
        viewer.addModel(pdbData, 'pdb');

        // Apply initial style
        applyStyle(viewer, currentStyle, position);

        // Add label for variant
        if (position) {
          viewer.addLabel(
            `${refAA || ''}${position}${altAA || ''}`,
            {
              position: { resi: position },
              backgroundColor: '#dc2626',
              fontColor: 'white',
              fontSize: 14,
              showBackground: true,
              backgroundOpacity: 0.9,
            },
            { resi: position, atom: 'CA' }
          );
          viewer.zoomTo({ resi: position }, 500);
        } else {
          viewer.zoomTo();
        }

        viewer.render();
        console.log('✅ Structure rendered successfully');

        // Handle resize to keep canvas within container
        const handleResize = () => {
          if (viewerInstance.current && viewerRef.current) {
            viewerInstance.current.resize();
            viewerInstance.current.render();
          }
        };
        const resizeObserver = new ResizeObserver(handleResize);
        if (viewerRef.current?.parentElement) {
          resizeObserver.observe(viewerRef.current.parentElement);
        }
        window.addEventListener('resize', handleResize);

        if (mounted) {
          setLoading(false);
        }

        // Store cleanup refs
        (viewerInstance as any)._resizeObserver = resizeObserver;
        (viewerInstance as any)._handleResize = handleResize;
      } catch (err) {
        console.error('❌ Structure viewer error:', err);
        if (mounted) {
          setError(err instanceof Error ? err.message : 'Failed to load structure');
          setLoading(false);
        }
      }
    };

    initViewer();

    return () => {
      mounted = false;
      if ((viewerInstance as any)._resizeObserver) {
        (viewerInstance as any)._resizeObserver.disconnect();
      }
      if ((viewerInstance as any)._handleResize) {
        window.removeEventListener('resize', (viewerInstance as any)._handleResize);
      }
      if (viewerInstance.current) {
        viewerInstance.current.clear();
        viewerInstance.current = null;
      }
    };
  }, [is3DmolLoaded, geneSymbol, proteinChange, providedUniprotId, getUniprotId, parseProteinChange, fetchStructure, applyStyle, currentStyle]);

  // Update style when changed
  useEffect(() => {
    if (viewerInstance.current && structureInfo && !loading) {
      applyStyle(viewerInstance.current, currentStyle, structureInfo.variantPosition);
    }
  }, [currentStyle, applyStyle, structureInfo, loading]);

  // Control functions
  const handleReset = () => {
    if (viewerInstance.current) {
      viewerInstance.current.zoomTo();
      viewerInstance.current.render();
    }
  };

  const handleZoomToVariant = () => {
    if (viewerInstance.current && structureInfo?.variantPosition) {
      viewerInstance.current.zoomTo({ resi: structureInfo.variantPosition }, 500);
      viewerInstance.current.render();
    }
  };

  const handleToggleSpin = () => {
    if (viewerInstance.current) {
      if (isSpinning) {
        viewerInstance.current.spin(false);
      } else {
        viewerInstance.current.spin('y', 1);
      }
      setIsSpinning(!isSpinning);
    }
  };

  const handleToggleSurface = () => {
    setShowSurface(!showSurface);
    if (viewerInstance.current) {
      if (!showSurface) {
        viewerInstance.current.addSurface(
          window.$3Dmol?.SurfaceType?.VDW || 1,
          { opacity: 0.7, color: 'white' },
          { hetflag: false }
        );
      } else {
        viewerInstance.current.removeAllSurfaces();
      }
      viewerInstance.current.render();
    }
  };

  const handleScreenshot = () => {
    if (viewerInstance.current) {
      const dataUrl = viewerInstance.current.pngURI();
      const link = document.createElement('a');
      link.download = `${geneSymbol}_${proteinChange || 'structure'}.png`;
      link.href = dataUrl;
      link.click();
    }
  };

  return (
    <div style={styles.container}>
      {/* CSS for spinner animation */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .structure-spinner {
          animation: spin 1s linear infinite;
        }
        .control-button:hover {
          background-color: #e2e8f0 !important;
        }
        .control-button:active {
          background-color: #cbd5e1 !important;
        }
        .style-button-active {
          background-color: #4f46e5 !important;
          color: white !important;
          border-color: #4f46e5 !important;
        }
      `}</style>

      {/* Viewer */}
      <div style={{ ...styles.viewerWrapper, height: `${height}px` }}>
        <div
          ref={viewerRef}
          style={{
            ...styles.viewer,
            display: loading || error ? 'none' : 'block',
          }}
        />

        {/* Loading State */}
        {loading && (
          <div style={styles.loadingOverlay}>
            <div className="structure-spinner" style={styles.spinner} />
            <p style={styles.loadingText}>Loading 3D structure for {geneSymbol}...</p>
            <p style={styles.loadingSubtext}>{loadingStatus}</p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div style={styles.errorOverlay}>
            <span style={styles.errorIcon}>⚠️</span>
            <p style={styles.errorText}>{error}</p>
            <p style={styles.errorHint}>
              The protein structure may not be available in AlphaFold Database.
            </p>
            <div style={styles.errorActions}>
              <a
                href={`https://alphafold.ebi.ac.uk/entry/${structureInfo?.uniprotId || geneSymbol}`}
                target="_blank"
                rel="noopener noreferrer"
                style={styles.errorLink}
              >
                🔬 Search AlphaFold Database
              </a>
              <a
                href={`https://www.rcsb.org/search?request=%7B%22query%22%3A%7B%22type%22%3A%22terminal%22%2C%22service%22%3A%22text%22%2C%22parameters%22%3A%7B%22value%22%3A%22${geneSymbol}%22%7D%7D%2C%22return_type%22%3A%22entry%22%7D`}
                target="_blank"
                rel="noopener noreferrer"
                style={styles.errorLink}
              >
                🏛️ Search PDB
              </a>
            </div>
          </div>
        )}
      </div>

      {/* Style Selector */}
      {!loading && !error && (
        <div style={styles.styleSelector}>
          <span style={styles.styleSelectorLabel}>View Style:</span>
          <div style={styles.styleButtons}>
            {(['cartoon', 'stick', 'sphere'] as ViewStyle[]).map((style) => (
              <button
                key={style}
                onClick={() => setCurrentStyle(style)}
                className={`control-button ${currentStyle === style ? 'style-button-active' : ''}`}
                style={{
                  ...styles.styleButton,
                  ...(currentStyle === style ? styles.styleButtonActive : {}),
                }}
              >
                {style.charAt(0).toUpperCase() + style.slice(1)}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Controls */}
      {!loading && !error && (
        <div style={styles.controls}>
          <button onClick={handleReset} className="control-button" style={styles.controlButton}>
            🔄 Reset View
          </button>
          {structureInfo?.variantPosition && (
            <button onClick={handleZoomToVariant} className="control-button" style={styles.controlButton}>
              🎯 Zoom to Variant
            </button>
          )}
          <button onClick={handleToggleSpin} className="control-button" style={styles.controlButton}>
            {isSpinning ? '⏹️ Stop Spin' : '🔁 Spin'}
          </button>
          <button onClick={handleToggleSurface} className="control-button" style={styles.controlButton}>
            {showSurface ? '👁️ Hide Surface' : '🫧 Show Surface'}
          </button>
          <button onClick={handleScreenshot} className="control-button" style={styles.controlButton}>
            📸 Screenshot
          </button>
        </div>
      )}

      {/* Legend */}
      {!loading && !error && (
        <div style={styles.legend}>
          <span style={styles.legendTitle}>AlphaFold Confidence (pLDDT):</span>
          <div style={styles.legendItems}>
            <span style={styles.legendItem}>
              <span style={{ ...styles.legendColor, backgroundColor: '#0053D6' }} />
              Very High (&gt;90)
            </span>
            <span style={styles.legendItem}>
              <span style={{ ...styles.legendColor, backgroundColor: '#65CBF3' }} />
              Confident (70-90)
            </span>
            <span style={styles.legendItem}>
              <span style={{ ...styles.legendColor, backgroundColor: '#FFDB13' }} />
              Low (50-70)
            </span>
            <span style={styles.legendItem}>
              <span style={{ ...styles.legendColor, backgroundColor: '#FF7D45' }} />
              Very Low (&lt;50)
            </span>
            {structureInfo?.variantPosition && (
              <span style={styles.legendItem}>
                <span style={{ ...styles.legendColor, backgroundColor: '#dc2626' }} />
                Variant Position
              </span>
            )}
          </div>
        </div>
      )}

      {/* Variant Info */}
      {!loading && !error && structureInfo?.variantPosition && (
        <div style={styles.variantInfo}>
          <div style={styles.variantInfoHeader}>
            <span style={styles.variantInfoIcon}>🧬</span>
            <span style={styles.variantInfoTitle}>Variant Location</span>
          </div>
          <p style={styles.variantInfoText}>
            The variant <strong>{structureInfo.refAA}{structureInfo.variantPosition}{structureInfo.altAA}</strong> is 
            highlighted in <span style={{ color: '#dc2626', fontWeight: 600 }}>red</span> on the protein structure.
            {structureInfo.refAA && structureInfo.altAA && (
              <> This represents a change from {getAminoAcidName(structureInfo.refAA)} to {getAminoAcidName(structureInfo.altAA)}.</>
            )}
          </p>
        </div>
      )}

      {/* External Links */}
      {!loading && !error && structureInfo && (
        <div style={styles.links}>
          <a
            href={`https://alphafold.ebi.ac.uk/entry/${structureInfo.uniprotId}`}
            target="_blank"
            rel="noopener noreferrer"
            style={styles.link}
          >
            🔬 AlphaFold Entry
          </a>
          <a
            href={`https://www.uniprot.org/uniprotkb/${structureInfo.uniprotId}`}
            target="_blank"
            rel="noopener noreferrer"
            style={styles.link}
          >
            📚 UniProt
          </a>
          <a
            href={`https://www.rcsb.org/search?request=%7B%22query%22%3A%7B%22type%22%3A%22terminal%22%2C%22service%22%3A%22text%22%2C%22parameters%22%3A%7B%22attribute%22%3A%22rcsb_polymer_entity_container_identifiers.reference_sequence_identifiers.database_accession%22%2C%22operator%22%3A%22exact_match%22%2C%22value%22%3A%22${structureInfo.uniprotId}%22%7D%7D%2C%22return_type%22%3A%22entry%22%7D`}
            target="_blank"
            rel="noopener noreferrer"
            style={styles.link}
          >
            🏛️ PDB Structures
          </a>
          {structureInfo.structureSource && (
            <span style={styles.sourceTag}>
              Source: {structureInfo.structureSource}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

// Helper function for amino acid names
function getAminoAcidName(code: string): string {
  const names: Record<string, string> = {
    A: 'Alanine', R: 'Arginine', N: 'Asparagine', D: 'Aspartic acid',
    C: 'Cysteine', E: 'Glutamic acid', Q: 'Glutamine', G: 'Glycine',
    H: 'Histidine', I: 'Isoleucine', L: 'Leucine', K: 'Lysine',
    M: 'Methionine', F: 'Phenylalanine', P: 'Proline', S: 'Serine',
    T: 'Threonine', W: 'Tryptophan', Y: 'Tyrosine', V: 'Valine',
    '*': 'Stop codon', X: 'Unknown',
  };
  return names[code] || code;
}

// Styles
const styles: { [key: string]: React.CSSProperties } = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    width: '100%',
    maxWidth: '100%',
    overflow: 'hidden',
    boxSizing: 'border-box' as const,
  },
  viewerWrapper: {
    position: 'relative',
    width: '100%',
    borderRadius: '12px',
    overflow: 'hidden',
    border: '1px solid #e2e8f0',
    backgroundColor: '#f8fafc',
  },
  viewer: {
    width: '100%',
    height: '100%',
  },
  loadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f8fafc',
  },
  spinner: {
    width: '40px',
    height: '40px',
    border: '4px solid #e2e8f0',
    borderTopColor: '#4f46e5',
    borderRadius: '50%',
  },
  loadingText: {
    marginTop: '16px',
    color: '#475569',
    fontSize: '14px',
    fontWeight: 500,
  },
  loadingSubtext: {
    marginTop: '4px',
    color: '#94a3b8',
    fontSize: '12px',
  },
  errorOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#fef2f2',
    padding: '20px',
    textAlign: 'center',
  },
  errorIcon: {
    fontSize: '40px',
    marginBottom: '12px',
  },
  errorText: {
    color: '#991b1b',
    fontSize: '14px',
    fontWeight: 500,
    margin: '0 0 8px 0',
  },
  errorHint: {
    color: '#b91c1c',
    fontSize: '13px',
    margin: '0 0 16px 0',
  },
  errorActions: {
    display: 'flex',
    gap: '12px',
    flexWrap: 'wrap',
    justifyContent: 'center',
  },
  errorLink: {
    color: '#4f46e5',
    fontSize: '13px',
    textDecoration: 'none',
    fontWeight: 500,
    padding: '8px 16px',
    backgroundColor: '#eef2ff',
    borderRadius: '8px',
    transition: 'background-color 0.2s',
  },
  styleSelector: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flexWrap: 'wrap',
  },
  styleSelectorLabel: {
    fontSize: '13px',
    fontWeight: 500,
    color: '#475569',
  },
  styleButtons: {
    display: 'flex',
    gap: '4px',
  },
  styleButton: {
    padding: '6px 12px',
    fontSize: '12px',
    color: '#475569',
    backgroundColor: '#f1f5f9',
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  styleButtonActive: {
    backgroundColor: '#4f46e5',
    color: 'white',
    borderColor: '#4f46e5',
  },
  controls: {
    display: 'flex',
    gap: '8px',
    flexWrap: 'wrap',
  },
  controlButton: {
    padding: '8px 12px',
    fontSize: '13px',
    color: '#334155',
    backgroundColor: '#f1f5f9',
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  legend: {
    padding: '12px',
    backgroundColor: '#f8fafc',
    borderRadius: '8px',
    border: '1px solid #e2e8f0',
  },
  legendTitle: {
    fontSize: '12px',
    fontWeight: 600,
    color: '#475569',
    marginBottom: '8px',
    display: 'block',
  },
  legendItems: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '12px',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '11px',
    color: '#64748b',
  },
  legendColor: {
    width: '12px',
    height: '12px',
    borderRadius: '2px',
    display: 'inline-block',
  },
  variantInfo: {
    padding: '12px 16px',
    backgroundColor: '#fef2f2',
    borderRadius: '8px',
    border: '1px solid #fecaca',
  },
  variantInfoHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '8px',
  },
  variantInfoIcon: {
    fontSize: '16px',
  },
  variantInfoTitle: {
    fontSize: '13px',
    fontWeight: 600,
    color: '#991b1b',
  },
  variantInfoText: {
    fontSize: '13px',
    color: '#7f1d1d',
    margin: 0,
    lineHeight: 1.5,
  },
  links: {
    display: 'flex',
    gap: '12px',
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  link: {
    fontSize: '13px',
    color: '#4f46e5',
    textDecoration: 'none',
    padding: '6px 12px',
    backgroundColor: '#eef2ff',
    borderRadius: '6px',
    transition: 'background-color 0.2s',
  },
  sourceTag: {
    fontSize: '11px',
    color: '#64748b',
    backgroundColor: '#f1f5f9',
    padding: '4px 8px',
    borderRadius: '4px',
    marginLeft: 'auto',
  },
};

export default ProteinStructureViewer;